/**
 * Renders an input UI based on the selected field's type for Detailed Site Search.
 * Supports text inputs and single-select dropdowns;
 *
 * @component
 * @param {Object} props
 * @param {Object} props.selectedField Current field selection state containing identifiers and values.
 * @param {string|null} props.selectedField.selectedFieldType The type of input to render (e.g., `text-field`, `select-field`).
 * @param {Array<{Name:string,ObjectId:string}>|null} props.selectedField.selectedFieldOptions Options for select inputs.
 * @param {string|null} props.selectedField.selectedFieldTextValue Current text value (for text inputs).
 * @param {Function} props.handleFieldChange Callback receiving `{ fieldValue, fieldType }` when the user edits a value.
 * @returns {JSX.Element|null} UI for the field type, or null if no type is selected.
 */

import { MinusCircleOutlined, PlusOutlined } from "@ant-design/icons";
import { Input } from "antd";
import { Typography } from "antd";
import dayjs from "dayjs";

import Button from "@ui/form-controls/button";
import Select from "@ui/form-controls/select";
import DRSDatePicker from "@ui/form-controls/date-picker";

import { FIELD_TYPES, MULTI_SELECT_FILTER_IDS, MULTI_TEXT_FILTER_IDS } from "./constants";

const { TEXT_FIELD, SELECT_FIELD, SELECT_DATE_FIELD, DATE_FIELD } = FIELD_TYPES;
const { Title } = Typography;

const FieldTypeGenerator = ({ selectedField, handleFieldChange }) => {
  const isMulti = MULTI_SELECT_FILTER_IDS.includes(selectedField.selectedFieldId);
  const isMultiText =
    MULTI_TEXT_FILTER_IDS.includes(selectedField.selectedFieldId) &&
    Array.isArray(selectedField.selectedFieldTextValue);
  const selectMode = isMulti ? "multiple" : "single";
  const textValues = isMultiText ? selectedField.selectedFieldTextValue : [];

  switch (selectedField.selectedFieldType) {
    case SELECT_FIELD:
      return (
        <>
          <Title level={5}>{isMulti ? "Select Value(s)" : "Select Value"}</Title>
          <Select
            key={selectedField.selectedFieldId}
            placeholder="Search to Select"
            callback={filter => handleFieldChange({ fieldValue: filter, fieldType: SELECT_FIELD })}
            optionsArray={selectedField.selectedFieldOptions || []}
            mode={selectMode}
            defaultValue={[]}
          />
        </>
      );
    case SELECT_DATE_FIELD:
      return (
        <>
          <Title level={5}>{isMulti ? "Select Value(s)" : "Select Value"}</Title>
          <Select
            key={selectedField.selectedFieldId}
            placeholder="Search to Select"
            callback={filter => handleFieldChange({ fieldValue: filter, fieldType: SELECT_FIELD })}
            optionsArray={selectedField.selectedFieldOptions || []}
            arrayValue={
              isMulti
                ? selectedField.selectedFieldDropDownValue || []
                : selectedField.selectedFieldDropDownValue
            }
            mode={selectMode}
            defaultValue={[]}
          />
          <Title level={5}>Enter Date</Title>
          <DRSDatePicker
            onChange={date => handleFieldChange({ fieldValue: date, fieldType: DATE_FIELD })}
            value={
              selectedField.selectedFieldDateValue
                ? dayjs(selectedField.selectedFieldDateValue, "YYYY-MM-DD")
                : null
            }
            className="w-full"
            defaultValue={null}
          />
        </>
      );
    case TEXT_FIELD:
      if (isMultiText) {
        return (
          <>
            <Title level={5}>Enter Value(s)</Title>
            {textValues.map((value, index) => (
              <div key={index} className="mt-2 flex items-center gap-2">
                <Input
                  onChange={fieldValue =>
                    handleFieldChange({ fieldValue, fieldType: TEXT_FIELD, index })
                  }
                  value={value}
                />
                {textValues.length > 1 && (
                  <Button
                    variant="ghost"
                    size="icon"
                    aria-label="Remove post code"
                    icon={<MinusCircleOutlined />}
                    onClick={() =>
                      handleFieldChange({ fieldType: TEXT_FIELD, action: "remove", index })
                    }
                  />
                )}
              </div>
            ))}
            <Button
              variant="outline"
              className="mt-2"
              icon={<PlusOutlined />}
              onClick={() => handleFieldChange({ fieldType: TEXT_FIELD, action: "add" })}
            >
              Add another post code
            </Button>
          </>
        );
      }

      return (
        <>
          <Title level={5}>Enter Value</Title>
          <Input
            onChange={fieldValue => handleFieldChange({ fieldValue, fieldType: TEXT_FIELD })}
            value={selectedField.selectedFieldTextValue}
            defaultValue=""
          />
        </>
      );
  }
};
export default FieldTypeGenerator;
